// Helper functions for the MagicAuthCode 6-digit verification field.

const NON_DIGIT = /\D/g
const SINGLE_DIGIT = /^\d$/

// Index of the last non-empty slot (-1 if all empty).
export function lastFilledIndex(code: string[]): number {
  for (let i = code.length - 1; i >= 0; i--) {
    if (code[i] !== '') {
      return i
    }
  }
  return -1
}

export function processPastedValue(
  value: string,
  currentCode: string[],
  startIndex: number,
): { newCode: string[]; nextFocusIndex: number } {
  if (value.length > 1) {
    // Handle paste - first extract only digits
    const digitsOnly = value.replace(NON_DIGIT, '').slice(0, 6 - startIndex)
    const newCode = [...currentCode]

    // Place digits consecutively starting from startIndex
    for (let i = 0; i < digitsOnly.length && startIndex + i < 6; i++) {
      const digit = digitsOnly[i]
      if (digit !== undefined) {
        newCode[startIndex + i] = digit
      }
    }

    // Focus last filled input or next empty one
    const lastIndex = lastFilledIndex(newCode)
    const nextIndex = Math.min(lastIndex + 1, 5)
    return { newCode, nextFocusIndex: nextIndex }
  }
  if (SINGLE_DIGIT.test(value)) {
    // Single digit input
    const newCode = [...currentCode]
    newCode[startIndex] = value
    // Move to next input
    const nextIndex = startIndex < 5 ? startIndex + 1 : startIndex
    return { newCode, nextFocusIndex: nextIndex }
  }
  return { newCode: currentCode, nextFocusIndex: startIndex }
}

export function processBackspace(
  currentCode: string[],
  currentIndex: number,
): { newCode: string[]; nextFocusIndex: number } {
  const newCode = [...currentCode]
  if (currentCode[currentIndex] !== '') {
    // Clear current input
    newCode[currentIndex] = ''
    return { newCode, nextFocusIndex: currentIndex }
  }
  if (currentIndex > 0) {
    // Move to previous input and clear it
    newCode[currentIndex - 1] = ''
    return { newCode, nextFocusIndex: currentIndex - 1 }
  }
  return { newCode, nextFocusIndex: currentIndex }
}

export function processClipboardPaste(pastedData: string): {
  newCode: string[]
  nextFocusIndex: number
} {
  const cleanedData = pastedData.replace(NON_DIGIT, '').slice(0, 6)
  if (cleanedData) {
    // Create array of 6 elements, filling with digits or empty strings
    const newCode = new Array(6).fill('').map((_, i) => cleanedData[i] || '')
    // Focus on the last filled input or the first empty one
    const lastIndex = lastFilledIndex(newCode)
    const nextIndex = Math.min(lastIndex + 1, 5)
    return { newCode, nextFocusIndex: nextIndex }
  }
  return { newCode: new Array(6).fill(''), nextFocusIndex: 0 }
}

export function isCodeComplete(code: string[]): boolean {
  return code.every((digit) => digit !== '') && code.join('').length === 6
}

export function isValidDigit(value: string): boolean {
  return SINGLE_DIGIT.test(value)
}
